import { useEffect, type ReactNode } from 'react';
import { T, useText } from '../lib/text';
import HeroCrest from './HeroCrest';

// Dark page banner shared by every inner page. Also owns the document title
// and meta description for its route.
//
// `id` is the page's wording prefix (e.g. "about"): the eyebrow, title, and
// subtitle become editable as <id>.eyebrow / <id>.title / <id>.sub, and the
// document title and meta description follow the edited wording too.
// `dynamicTitle` opts the title out of editing when it is computed per
// visitor (the member portal's "Welcome, <name>").
// `crest` adds the homepage's spinning ghost crest, large and faint, running
// off the banner's right edge (the member portal). `aside` puts a card on the
// right of the title (the portal's membership card); the title's text sets
// the banner's height either way. `wideAside` makes room for a bigger aside,
// the Program map's Florida (board, 2026-10-21: the whole state on the
// opening screen), and trims the banner's padding so it fits a laptop.
export default function PageHead({
  id,
  eyebrow,
  title,
  sub,
  dynamicTitle = false,
  crest = false,
  aside,
  wideAside = false,
}: {
  id: string;
  eyebrow: string;
  title: string;
  sub?: string;
  dynamicTitle?: boolean;
  crest?: boolean;
  aside?: ReactNode;
  wideAside?: boolean;
}) {
  const editedTitle = useText(`${id}.title`, title);
  const liveTitle = dynamicTitle ? title : editedTitle;
  // Only a page that has a subtitle lets an override drive its description;
  // a stray <id>.sub row must never set a meta description the banner
  // doesn't show.
  const editedSub = useText(`${id}.sub`, sub ?? '');
  const liveSub = sub ? editedSub : '';

  useEffect(() => {
    document.title = `${liveTitle} · FAEMSE`;
    // Keep the share-card tags in step with the page, not stuck on the
    // homepage values from index.html.
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', `${liveTitle} · FAEMSE`);
    document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', `${liveTitle} · FAEMSE`);
    if (liveSub) {
      document.querySelector('meta[name="description"]')?.setAttribute('content', liveSub);
      document.querySelector('meta[property="og:description"]')?.setAttribute('content', liveSub);
    }
  }, [liveTitle, liveSub]);

  return (
    <section className="velvet bg-ink text-white relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,.05) 1px, transparent 1.4px)',
          backgroundSize: '26px 26px',
        }}
      />
      <div
        className="absolute -right-40 -top-44 w-[560px] h-[560px] rounded-full opacity-[.13] blur-[85px] pointer-events-none bg-[radial-gradient(circle,rgba(245,206,90,.95),transparent_62%)]"
        aria-hidden
      />
      {crest && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="wrap relative h-full">
            <HeroCrest banner />
          </div>
        </div>
      )}
      {/* The title sits above the aside (z-10): the map's glow reaches about
          9% past its box, and on a phone it lay over the subtitle and
          washed it out. */}
      <div className={`wrap relative ${wideAside ? 'py-12 md:py-14' : 'py-16 md:py-20'} ${aside ? `flex flex-wrap items-center justify-between gap-x-10 ${wideAside ? 'gap-y-12' : 'gap-y-8'}` : ''}`}>
        <div className={aside ? 'relative z-10 flex-[1_1_420px] min-w-0' : ''}>
          <p className="eyebrow !text-brand-goldsoft">
            <T id={`${id}.eyebrow`}>{eyebrow}</T>
          </p>
          <h1 className="font-disp font-bold uppercase leading-[0.95] text-[clamp(40px,6vw,72px)] mt-3">
            {dynamicTitle ? title : <T id={`${id}.title`}>{title}</T>}
          </h1>
          {sub && (
            <p className="text-[#BCCBE7] text-lg max-w-[60ch] mt-4">
              <T id={`${id}.sub`}>{sub}</T>
            </p>
          )}
        </div>
        {/* When the wide aside wraps below the title it is centered and kept
            8px in from each side: the map's westernmost label (Pensacola)
            sits just outside its box, and flush with the page margin it ran
            3px off the screen from about 520px to 1100px wide (board,
            2026-11-03). Side by side, the title takes all the free space, so
            mx-auto changes nothing there. */}
        {aside && (
          <div className={`${wideAside ? 'flex-[0_1_520px] mx-auto min-[520px]:max-w-[calc(100%-16px)]' : 'flex-[0_1_380px]'} min-w-0`}>{aside}</div>
        )}
      </div>
    </section>
  );
}
